import { Button } from '../../components/Button';
import { Stepper } from '../../components/Stepper';
import { ChevronLeft } from '../../icons';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';

export interface DocumentBatchHeaderProps {
  /** 1-based position of the document being walked. */
  current: number;
  total: number;
  /** The document's own name, so the user knows WHICH one they are picking pages for. */
  name: string;
  /** "Document 2 of 4" — pre-translated. The DS carries no i18n. */
  stepLabel: string;
  /** Accessible name of the Back control. Pre-translated. */
  backLabel: string;
  /** Absent on the first document: there is nothing to go back to. */
  onBack?: () => void;
  className?: string;
  testID?: string;
}

/**
 * The strip above page selection during a MULTI-DOCUMENT upload: where the
 * user is in the walk, which document this is, and a way back to the previous
 * one. A DS `Stepper` (indicator dots — ten documents must still fit) carries
 * the position; the words say it too, because a row of dots is not a count.
 *
 * Shared so the phone and the iPad, when they take several documents at once,
 * show the same walk — the header is only DS parts, and nothing in it is
 * platform-bound.
 */
export function DocumentBatchHeader({
  current,
  total,
  name,
  stepLabel,
  backLabel,
  onBack,
  className,
  testID,
}: DocumentBatchHeaderProps) {
  return (
    <View className={cn('w-full flex-row items-center gap-3', className)} testID={testID}>
      {onBack ? (
        <Button
          size="s"
          variant="secondary"
          icon={ChevronLeft}
          accessibilityLabel={backLabel}
          onPress={onBack}
          testID={testID ? `${testID}-back` : undefined}
        />
      ) : null}
      <Stepper
        type="indicator"
        size="small"
        currentStep={current - 1}
        steps={Array.from({ length: total }, () => ({}))}
        className="shrink-0"
      />
      <View className="min-w-0 shrink flex-col">
        <Text
          className="text-sm font-semibold text-secondary-800 native:font-mona-semibold"
          testID={testID ? `${testID}-step` : undefined}>
          {stepLabel}
        </Text>
        <Text numberOfLines={1} className="text-sm text-fg-subtle">
          {name}
        </Text>
      </View>
    </View>
  );
}
(DocumentBatchHeader as { displayName?: string }).displayName = 'DocumentBatchHeader';
